import supabaseDAL from './supabaseDAL';
import * as Sentry from '@sentry/react';

// Table name constant - match exact case from Supabase
const TABLE_NAME = 'tblblasterhighscore';

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Validates a UUID string
 * @param {string} uuid - The UUID to validate
 * @returns {boolean} Whether the UUID is valid
 */
const isValidUUID = (uuid) => {
  return UUID_REGEX.test(uuid);
};

/**
 * Service module for interacting with the BlasterHighScore table
 */
const BlasterHighScoreService = {
  /**
   * Get high scores with optional filters
   * @param {Object} options - Query options
   * @param {number} [options.limit=10] - Maximum number of records to return
   * @param {number} [options.offset=0] - Number of records to skip
   * @param {Object} [options.filter={}] - Filter criteria (e.g., { level: 5 })
   * @param {string} [options.orderBy='score'] - Column to order by
   * @param {boolean} [options.ascending=false] - Order direction (false=descending)
   * @returns {Promise<{data: Array, error: Object}>} Query result
   */
  async getHighScores(options = {}) {
    try {
      const {
        limit = 10,
        offset = 0,
        filter = {},
        orderBy = 'score',
        ascending = false
      } = options;

      // Query the table
      let query = supabaseDAL.client
        .from(TABLE_NAME)
        .select('*');

      // Apply filters
      Object.entries(filter).forEach(([column, value]) => {
        query = query.eq(column, value);
      });

      // Apply ordering
      query = query.order(orderBy, { ascending });

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      const { data, error } = await query;

      if (error) throw error;
      
      
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching high scores:', error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("highScores", {
          options: JSON.stringify(options),
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Get top scores grouped by level
   * @param {number} [limit=3] - Number of top scores per level
   * @returns {Promise<{data: Object, error: Object}>} Query result
   */
  async getTopScoresByLevel(limit = 3) {
    try {
      // This is a more complex query that needs custom SQL
      // Get distinct levels first
      const { data: levels, error: levelsError } = await supabaseDAL.client
        .from(TABLE_NAME)
        .select('level')
        .order('level')
        .is('level', 'not.null');

      if (levelsError) throw levelsError;
      if (!levels || levels.length === 0) return { data: {}, error: null };

      // For each level, get the top scores
      const result = {};
      for (const { level } of levels) {
        const { data: scores, error } = await supabaseDAL.client
          .from(TABLE_NAME)
          .select('*')
          .eq('level', level)
          .order('score', { ascending: false })
          .limit(limit);

        if (error) throw error;
        result[level] = scores;
      }

      return { data: result, error: null };
    } catch (error) {
      console.error('Error fetching top scores by level:', error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("topScoresByLevel", {
          limit,
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Get a single high score by ID
   * @param {string} id - The UUID of the high score
   * @returns {Promise<{data: Object, error: Object}>} Query result
   */
  async getHighScoreById(id) {
    try {
      if (!id) throw new Error('ID is required');

      const { data, error } = await supabaseDAL.client
        .from(TABLE_NAME)
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error(`Error fetching high score with ID ${id}:`, error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("highScoreById", {
          id,
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Get high scores for a specific user
   * @param {string} userId - The user ID or null for local user
   * @param {string} [localUserId] - Optional local user ID for non-authenticated users
   * @returns {Promise<{data: Array, error: Object}>} Query result
   */
  async getUserHighScores(userId = null, localUserId = null) {
    try {
      // Validate UUID format if provided
      if (userId && !isValidUUID(userId)) {
        throw new Error('Invalid user_id UUID format');
      }
      if (localUserId && !isValidUUID(localUserId)) {
        throw new Error('Invalid local_user_id UUID format');
      }

      let query = supabaseDAL.client.from(TABLE_NAME).select('*');

      if (userId) {
        // For authenticated users
        query = query.eq('user_id', userId);
      } else if (localUserId) {
        // For local users
        query = query.eq('local_user_id', localUserId);
      } else {
        throw new Error('Either userId or localUserId must be provided');
      }

      const { data, error } = await query.order('score', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user high scores:', error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("userHighScores", {
          userId: userId || "null",
          localUserId: localUserId || "null",
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Add a new high score
   * @param {Object} highScore - The high score to add
   * @param {number} highScore.level - The game level
   * @param {number} highScore.score - The score achieved
   * @param {string} [highScore.local_user_id] - Local user ID for non-authenticated users
   * @param {string} [highScore.struserinitials] - User's initials (optional)
   * @returns {Promise<{data: Object, error: Object}>} Insert result
   */
  async addHighScore(highScore) {
    try {
      if (!highScore) throw new Error('High score data is required');
      if (highScore.score === undefined || highScore.score === null) {
        throw new Error('Score is required');
      }
      if (highScore.level === undefined || highScore.level === null) {
        throw new Error('Level is required');
      }
      if (!highScore.local_user_id) {
        throw new Error('local_user_id is required');
      }

      // Validate UUID format
      if (!isValidUUID(highScore.local_user_id)) {
        throw new Error('Invalid local_user_id UUID format');
      }

      // Create the params for the stored procedure
      const params = {
        p_score: highScore.score,
        p_local_user_id: highScore.local_user_id,
        p_level: highScore.level,
        p_initials: highScore.struserinitials || null
      };

      // Call the stored procedure - minimize database operations during gameplay
      const { data, error } = await supabaseDAL.client
        .rpc('submit_high_score', params);

      if (error) {
        console.error('Error submitting high score:', error.message);
        
        // Track database errors in Sentry with context
        if (Sentry && Sentry.captureException) {
          Sentry.setContext("addHighScore", {
            level: highScore.level,
            score: highScore.score,
            hasInitials: !!highScore.struserinitials,
            error: error.message,
            procedure: 'submit_high_score'
          });
          Sentry.captureException(error);
        }
        
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error adding high score:', error.message || error);
      
      // Track all errors in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("addHighScore", {
          level: highScore?.level,
          score: highScore?.score,
          hasLocalUserId: !!highScore?.local_user_id,
          hasInitials: !!highScore?.struserinitials,
          errorMessage: error.message || 'Unknown error'
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Update an existing high score
   * @param {string} id - The UUID of the high score to update
   * @param {Object} updates - The fields to update
   * @returns {Promise<{data: Object, error: Object}>} Update result
   */
  async updateHighScore(id, updates) {
    try {
      if (!id) throw new Error('ID is required');
      if (!updates || Object.keys(updates).length === 0) {
        throw new Error('Update data is required');
      }

      // Only allow valid fields to be updated
      const validUpdates = {};
      const allowedFields = ['score', 'level', 'struserinitials'];
      
      for (const field of allowedFields) {
        if (updates[field] !== undefined) {
          validUpdates[field] = updates[field];
        }
      }
      
      if (Object.keys(validUpdates).length === 0) {
        throw new Error('No valid fields to update');
      }

      const { data, error } = await supabaseDAL.client
        .from(TABLE_NAME)
        .update(validUpdates)
        .eq('id', id)
        .select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error(`Error updating high score with ID ${id}:`, error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("updateHighScore", {
          id,
          updates: JSON.stringify(updates),
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  },

  /**
   * Delete a high score
   * @param {string} id - The UUID of the high score to delete
   * @returns {Promise<{success: boolean, error: Object}>} Delete result
   */
  async deleteHighScore(id) {
    try {
      if (!id) throw new Error('ID is required');

      const { error } = await supabaseDAL.client
        .from(TABLE_NAME)
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      return { success: true, error: null };
    } catch (error) {
      console.error(`Error deleting high score with ID ${id}:`, error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("deleteHighScore", {
          id,
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { success: false, error };
    }
  },

  /**
   * Check if a score qualifies as a new high score
   * @param {number} level - The game level
   * @param {number} score - The score to check
   * @param {string} [userId] - User ID for authenticated users
   * @param {string} [localUserId] - Local user ID for non-authenticated users
   * @returns {Promise<{isHighScore: boolean, rank: number, error: Object}>} Check result
   */
  async isNewHighScore(level, score, userId = null, localUserId = null) {
    try {
      if (score === undefined || score === null) {
        throw new Error('Score is required');
      }
      if (level === undefined || level === null) {
        throw new Error('Level is required');
      }

      // Get top 10 scores for this level
      const { data, error } = await supabaseDAL.client
        .from(TABLE_NAME)
        .select('score')
        .eq('level', level)
        .order('score', { ascending: false })
        .limit(10);

      if (error) throw error;

      // If less than 10 scores, it's automatically a high score
      if (!data || data.length < 10) {
        return { isHighScore: true, rank: data ? data.length + 1 : 1, error: null };
      }

      // Check if the score beats the lowest in top 10
      const lowestScore = data[data.length - 1].score;
      if (score > lowestScore) {
        // Find the rank
        const rank = data.findIndex(item => score > item.score) + 1;
        return { isHighScore: true, rank, error: null };
      }

      return { isHighScore: false, rank: null, error: null };
    } catch (error) {
      console.error('Error checking for new high score:', error);
      
      // Track error in Sentry with context
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("isNewHighScore", {
          level,
          score,
          userId: userId || "null",
          localUserId: localUserId || "null",
          table: TABLE_NAME
        });
        Sentry.captureException(error);
      }
      
      return { isHighScore: false, rank: null, error };
    }
  }
};

export default BlasterHighScoreService; 